import React, { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Step4Props } from './types';
import { Step4ValueModel } from '../types';
import { validateStep4 } from '../Validation';
import ContactPerson from '../../Forms/ContactPerson';
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import { getAllErrors, removeAllErrorsForKey } from "@sharedComponents/PantaForm/utils";
import Alert from "@sharedComponents/Alert";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import Icon from "@sharedComponents/Icon";

function onChange(
    key: string,
    stepValues: Step4ValueModel,
    values: any,
    errors: FormErrorType[],
    setFormErrors: Function,
    setStepValues: Function,
    onChangeValues?: Function
) {
    const keys = Object.keys(values)

    keys.forEach((key: string) => {
        errors = removeAllErrorsForKey(errors, key)
    })

    setFormErrors(errors)

    const newValues = {
        ...stepValues[key],
        ...values,
    }

    setStepValues(
        (() => {
            const values = { ...stepValues }
            values[key] = newValues
            return values
        })()
    )

    onChangeValues &&
        onChangeValues(
            (() => {
                const values: any = {}
                values[key] = newValues
                return values
            })()
        )
}

const Step4: FunctionComponent<Step4Props> = props => {
    const { t } = useTranslation()

    const [contactPersonFormErrors, setContactPersonFormErrors] = useState<
        FormErrorType[]
    >([])

    const [reportPersonFormErrors, setReportPersonFormErrors] = useState<
        FormErrorType[]
    >([])

    const allErrors = getAllErrors(
        contactPersonFormErrors.concat(reportPersonFormErrors)
    )

    const [stepValues, setStepValues] = useState<Step4ValueModel>({
        contactPerson: {},
        reportPerson: {},
    })

    const rehydrate = (values: any) => {
        if (values) {
            setStepValues({
                contactPerson: values.contactPerson || {},
                reportPerson: values.reportPerson || {},
            } as Step4ValueModel)
        }
    }

    useEffect(() => {
        rehydrate(props.values)
    }, [props.rehydrateFormKey])

    useEffect(() => {
        rehydrate(props.values)
    }, [])

    const onChangeContactPerson = (values: any) => {
        onChange(
            'contactPerson',
            stepValues,
            values,
            contactPersonFormErrors,
            setContactPersonFormErrors,
            setStepValues,
            props.onChangeValues
        )
    }

    const onChangeReportPerson = (values: any) => {
        onChange(
            'reportPerson',
            stepValues,
            values,
            reportPersonFormErrors,
            setReportPersonFormErrors,
            setStepValues,
            props.onChangeValues
        )
    }

    const copyContactPersonToReportPerson = () => {
        const newStepValues = {
            ...stepValues,
            reportPerson: {
                ...stepValues.contactPerson,
            },
        }

        setStepValues(newStepValues)

        props.onChangeValues &&
            props.onChangeValues({
                reportPerson: {
                    ...stepValues.contactPerson,
                },
            })

        validateStep4(
            newStepValues,
            contactPersonFormErrors,
            reportPersonFormErrors,
            props.hideReportPersonForm
        ).then((errors: FormErrorType[][]) => {
            if (errors.length >= 2) {
                const [contactPersonFormErrors, reportPersonFormErrors] = errors;
                setContactPersonFormErrors(contactPersonFormErrors || [])
                setReportPersonFormErrors(reportPersonFormErrors || [])
            }
        })
    }

    const nextStep = () => {
        validateStep4(
            stepValues,
            contactPersonFormErrors,
            reportPersonFormErrors,
            props.hideReportPersonForm
        ).then((errors: FormErrorType[][]) => {
            if (errors.length >= 2) {
                const [contactPersonFormErrors, reportPersonFormErrors] = errors;
                setContactPersonFormErrors(contactPersonFormErrors || [])
                setReportPersonFormErrors(reportPersonFormErrors || [])

                const allErrors = getAllErrors(
                  contactPersonFormErrors.concat(reportPersonFormErrors)
                )

                if (allErrors.length > 0) {
                    return
                }

                props.onChangeValues && props.onChangeValues({})
                props.nextStep && props.nextStep()
            }
        })
    }

    const prevStep = () => {
        setContactPersonFormErrors([])
        setReportPersonFormErrors([])

        props.prevStep && props.prevStep()
    }

    const onCancel = () => {
        setContactPersonFormErrors([])
        setReportPersonFormErrors([])

        props.onCancel && props.onCancel()
    }

    return (
        <div className='form-step-wrapper form-step-wrapper--wide'>
            <h3>{props.heading}</h3>
            <p className="required-asterisk mt-3">{t('global.requiredAsterisk')}</p>

            {props.cmsContent ? (
                <div className='form-step-wrapper__content'>
                    {props.cmsContent}
                </div>
            ) : (
                ''
            )}
            <ContactPerson
                id="contact-person"
                title={t('companyRegistration.customer.contactPerson')}
                values={stepValues.contactPerson}
                formErrors={contactPersonFormErrors}
                onChangeValues={onChangeContactPerson}
            />
            {!props.hideReportPersonForm && (
                <ContactPerson
                    id="report-person"
                    title={t('companyRegistration.customer.reportPerson')}
                    values={stepValues.reportPerson}
                    formErrors={reportPersonFormErrors}
                    onChangeValues={onChangeReportPerson}
                    pasteButtonText={t(
                        'companyRegistration.customer.copyFromContactPerson'
                    )}
                    onPaste={copyContactPersonToReportPerson}
                />
            )}
            {allErrors.length > 0 && (
                <div className='form-step-wrapper__error'>
                    <Alert
                        type="danger"
                        text={t(
                            'companyRegistration.companyInfoContainsErrors'
                        )}
                        subtext={allErrors}
                        compact={false}
                    />
                </div>
            )}
            <div className='buttons-wrapper'>
                <Button
                    type={ButtonType.PRIMARY}
                    className="next-button"
                    text={
                        <span className='buttons-wrapper__button-text'>
                            <span>{t('global.preview')}</span>
                        </span>
                    }
                    onClick={nextStep}
                />
                <Button
                    type={ButtonType.PRIMARY}
                    className="prev-button"
                    text={
                        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--prev'>
                            <Icon iconName="arrow-left" />
                            <span>{t('button.previous')}</span>
                        </span>
                    }
                    onClick={prevStep}
                />
                <Button
                    type={ButtonType.SECONDARY}
                    className="cancel-button"
                    text={
                        <span className='buttons-wrapper__button-text'>
                            <Icon iconName="close" />
                            <span>{t('button.cancel')}</span>
                        </span>
                    }
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

export default Step4